import { treaty } from "@elysiajs/eden";
import type { App } from "@ichegou/api/app";

/** Same-origin `/api` (Vite proxy locally, reverse proxy in production) keeps auth cookies first-party. */
export const API_BASE = `${window.location.origin}/api`;

// parseDate off: the API sends ISO strings and the types say string, so keep them strings.
export const api = treaty<App>(API_BASE, { fetch: { credentials: "include" }, parseDate: false });

/** Error carrying the API's `{ error }` code, e.g. "two_factor_required" or "forbidden". */
export class ApiError extends Error {
  constructor(public status: number, public code: string, public body: unknown) {
    super(code);
  }
}

type EdenResult<T> = { data: T; error: null } | { data: null; error: { status: unknown; value: unknown } };

/** Unwraps an Eden call: returns data or throws ApiError with the server's error code. */
export async function call<T>(p: Promise<EdenResult<T>>): Promise<Exclude<T, null>> {
  const r = await p;
  if (r.error) {
    const value = r.error.value as { error?: string } | string | null;
    const code = typeof value === "object" && value?.error ? value.error : String(r.error.status);
    throw new ApiError(Number(r.error.status), code, value);
  }
  return r.data as Exclude<T, null>;
}

/** Better Auth endpoints are mounted, so Eden has no types for them; plain fetch. */
export async function authPost<T = unknown>(path: string, body: unknown = {}): Promise<T> {
  const res = await fetch(`${API_BASE}/auth${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, (json as { code?: string } | null)?.code ?? String(res.status), json);
  return json as T;
}
